import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { PresenceUser } from "@/hooks/usePresence";

function getInitials(user: PresenceUser): string {
  if (user.full_name) {
    return user.full_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return "?";
}

const STATUS_COLORS: Record<string, string> = {
  online: "bg-green-500",
  typing: "bg-amber-400 animate-pulse",
  idle: "bg-muted-foreground/50",
};

export function PresenceAvatars({
  users,
  maxVisible = 5,
  size = "sm",
}: {
  users: PresenceUser[];
  maxVisible?: number;
  size?: "sm" | "md";
}) {
  if (users.length === 0) return null;

  const visible = users.slice(0, maxVisible);
  const overflow = users.length - maxVisible;
  const dims = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs";
  const dotSize = size === "sm" ? "w-2 h-2" : "w-2.5 h-2.5";

  return (
    <div className="flex items-center -space-x-2">
      {visible.map((user) => (
        <Tooltip key={user.user_id}>
          <TooltipTrigger asChild>
            <div className="relative">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name || "User"}
                  className={`${dims} rounded-full border-2 border-background object-cover`}
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div
                  className={`${dims} rounded-full border-2 border-background bg-primary/20 text-primary font-semibold flex items-center justify-center`}
                >
                  {getInitials(user)}
                </div>
              )}
              <span
                className={`absolute -bottom-0.5 -right-0.5 ${dotSize} rounded-full border border-background ${STATUS_COLORS[user.status] || STATUS_COLORS.online}`}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            <p className="font-medium">{user.full_name || "Anonymous"}</p>
            {user.current_action && (
              <p className="text-muted-foreground">{user.current_action}</p>
            )}
            <p className="text-muted-foreground capitalize">{user.status}</p>
          </TooltipContent>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <div
          className={`${dims} rounded-full border-2 border-background bg-muted text-muted-foreground font-medium flex items-center justify-center`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
